import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { Plus, Trash2, ListChecks, Package, X, Save, Pencil, AlertTriangle, CheckCircle2 } from "lucide-react";

/**
 * Company-wide checklists and materials management.
 * Used inside Asetukset (CompanySettings page).
 * Data belongs to the authenticated user (company).
 */
export default function CompanyCatalogs() {
  // Yrityksen checklistit poistettu — sama toiminnallisuus on nyt Työpohjat-osiossa
  // (/app/tyopohjat), joka on selkeämpi ja rakennettu builder-tyylisesti.
  return (
    <div className="space-y-8">
      <MaterialsSection />
    </div>
  );
}

// ---------------- Checklists ----------------
function ChecklistsSection() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(null); // null = closed, {} = new, {...} = edit existing

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/company/checklists");
      setItems(data.checklists || []);
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => { load(); }, []);

  const remove = async (id) => {
    if (!confirm("Poistetaanko tämä checklist?")) return;
    await api.delete(`/company/checklists/${id}`);
    toast.success("Poistettu");
    load();
  };

  return (
    <section className="rounded-2xl border border-slate-100 bg-white p-6 shadow-sm" data-testid="company-checklists-section">
      <div className="flex items-center gap-3 mb-1">
        <div className="h-9 w-9 rounded-lg bg-emerald-50 text-emerald-600 flex items-center justify-center">
          <ListChecks className="h-5 w-5" />
        </div>
        <div className="flex-1">
          <div className="font-display font-semibold text-slate-900">Checklistit ja raporttipohjat</div>
          <div className="text-xs text-slate-500">Luo valmiita työvaihelistoja ja pohjia kenttäraportointiin. Kenttätyöntekijä voi käyttää niitä raportin luonnissa.</div>
        </div>
        {editing === null && (
          <button onClick={() => setEditing({})} className="btn-primary text-sm" data-testid="checklist-add">
            <Plus className="h-4 w-4" /> Uusi checklist / pohja
          </button>
        )}
      </div>

      {editing !== null && (
        <ChecklistEditor
          initial={editing}
          onClose={() => setEditing(null)}
          onSaved={() => { setEditing(null); load(); }}
        />
      )}

      <div className="mt-4 space-y-2">
        {loading && <div className="text-sm text-slate-400 py-3">Ladataan…</div>}
        {!loading && items.length === 0 && editing === null && (
          <div className="text-sm text-slate-500 py-3 italic">Ei vielä pohjia. Luo ensimmäinen yläpuolella.</div>
        )}
        {items.map((cl) => {
          const isFinding = cl.kind === "findings";
          return (
            <div key={cl.checklist_id} className={`rounded-xl border p-3 ${isFinding ? "border-amber-200 bg-amber-50/50" : "border-slate-100 bg-slate-50/50"}`} data-testid={`checklist-row-${cl.checklist_id}`}>
              <div className="flex items-center gap-2">
                <div className="font-semibold text-slate-900 text-sm flex-1 truncate flex items-center gap-2">
                  {cl.name}
                  {isFinding && (
                    <span className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded-md text-[10px] font-bold uppercase tracking-wider bg-amber-200 text-amber-900">
                      <AlertTriangle className="h-3 w-3" /> Puute
                    </span>
                  )}
                </div>
                <span className="text-xs text-slate-500">{cl.items.length} kpl</span>
                <button onClick={() => setEditing(cl)} className="p-1.5 rounded-lg hover:bg-white text-slate-500" data-testid={`checklist-edit-${cl.checklist_id}`}>
                  <Pencil className="h-4 w-4" />
                </button>
                <button onClick={() => remove(cl.checklist_id)} className="p-1.5 rounded-lg hover:bg-white text-rose-500" data-testid={`checklist-delete-${cl.checklist_id}`}>
                  <Trash2 className="h-4 w-4" />
                </button>
              </div>
              {cl.items.length > 0 && (
                <div className="mt-2 flex flex-wrap gap-1.5">
                  {cl.items.map((it, i) => {
                    const itemFinding = typeof it === "string" ? isFinding : !!it.is_finding;
                    const label = typeof it === "string" ? it : (it.label || "");
                    return (
                      <span key={i} className={`inline-flex items-center text-xs px-2 py-0.5 rounded-md border ${itemFinding ? "bg-white border-amber-200 text-amber-900" : "bg-white border-slate-200 text-slate-700"}`}>
                        {label}
                      </span>
                    );
                  })}
                </div>
              )}
            </div>
          );
        })}
      </div>
    </section>
  );
}

function ChecklistEditor({ initial, onClose, onSaved }) {
  const [name, setName] = useState(initial.name || "");
  // Normalize legacy items (List[str]) to modern format {label, is_finding}.
  // Backend now returns objects, but tolerate both for robustness.
  const initialItems = (initial.items || []).map((it) =>
    typeof it === "string"
      ? { label: it, is_finding: initial.kind === "findings" }
      : { label: it.label || "", is_finding: !!it.is_finding }
  );
  const [items, setItems] = useState(initialItems);
  // `defaultKind` is the type assigned to new items by default ("normal" tai "findings")
  // — vain käyttömukavuutta, ei muuta jo lisättyjä itemejä.
  const [defaultKind, setDefaultKind] = useState(initial.kind || "normal");
  const [newItem, setNewItem] = useState("");
  const [busy, setBusy] = useState(false);

  const addItem = () => {
    const v = newItem.trim();
    if (!v) return;
    if (items.some((it) => it.label === v)) { toast.error("Kohde on jo listassa"); return; }
    setItems([...items, { label: v, is_finding: defaultKind === "findings" }]);
    setNewItem("");
  };
  const toggleItemFinding = (idx) => {
    setItems(items.map((it, i) => (i === idx ? { ...it, is_finding: !it.is_finding } : it)));
  };
  const save = async () => {
    if (!name.trim()) { toast.error("Anna pohjan nimi"); return; }
    setBusy(true);
    try {
      // Send the object-form items; backend normalizes again for safety.
      // `kind` jätetään mukaan vain backwards-compat-ympäristössä — varsinainen
      // tieto on per-item is_finding-kentässä.
      const payload = {
        name: name.trim(),
        items,
        kind: defaultKind,
      };
      if (initial.checklist_id) {
        await api.patch(`/company/checklists/${initial.checklist_id}`, payload);
      } else {
        await api.post("/company/checklists", payload);
      }
      toast.success("Tallennettu");
      onSaved();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Tallennus epäonnistui");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="mt-4 rounded-xl border border-slate-200 bg-white p-4" data-testid="checklist-editor">
      <div className="flex items-center gap-2 mb-3">
        <input
          autoFocus
          value={name}
          onChange={(e) => setName(e.target.value)}
          placeholder="Pohjan nimi / checklistin nimi (esim. IV-kausihuolto, Vuosihuolto, LVI-kartoitus)"
          className="input-base flex-1"
          data-testid="checklist-name-input"
        />
        <button onClick={onClose} className="p-2 rounded-lg hover:bg-white text-slate-500" data-testid="checklist-editor-close">
          <X className="h-4 w-4" />
        </button>
      </div>

      {/* Oletustyypin valinta — koskee vain UUSIA itemejä joita lisäät tämän
          jälkeen. Jo lisätyt itemit säilyttävät oman tyyppinsä. */}
      <div className="mb-3 grid grid-cols-2 gap-2">
        <button
          type="button"
          onClick={() => setDefaultKind("normal")}
          className={`px-3 py-2.5 rounded-lg border text-left transition-colors ${
            defaultKind === "normal"
              ? "border-emerald-400 bg-emerald-50/60 shadow-sm"
              : "border-slate-200 bg-white hover:bg-slate-50"
          }`}
          data-testid="checklist-kind-normal"
        >
          <div className="flex items-center gap-1.5 text-sm font-semibold text-slate-900">
            <CheckCircle2 className="h-4 w-4 text-emerald-600" /> Tehdyt toimenpiteet
          </div>
          <div className="text-xs text-slate-500 mt-0.5">Uusi lisätty kohta on oletuksena työvaihe.</div>
        </button>
        <button
          type="button"
          onClick={() => setDefaultKind("findings")}
          className={`px-3 py-2.5 rounded-lg border text-left transition-colors ${
            defaultKind === "findings"
              ? "border-amber-400 bg-amber-50/60 shadow-sm"
              : "border-slate-200 bg-white hover:bg-slate-50"
          }`}
          data-testid="checklist-kind-findings"
        >
          <div className="flex items-center gap-1.5 text-sm font-semibold text-slate-900">
            <AlertTriangle className="h-4 w-4 text-amber-600" /> Havainnot
          </div>
          <div className="text-xs text-slate-500 mt-0.5">Uusi lisätty kohta on oletuksena havainto.</div>
        </button>
      </div>
      <div className="text-xs text-slate-500 mb-2">
        Voit muokata kunkin kohdan tyyppiä yksitellen kohdan vieressä olevasta painikkeesta.
      </div>
      <div className="space-y-1.5">
        {items.map((it, i) => (
          <div
            key={i}
            className={`flex items-center gap-2 rounded-lg px-3 py-1.5 border ${
              it.is_finding ? "bg-amber-50/60 border-amber-200" : "bg-emerald-50/40 border-emerald-200"
            }`}
            data-testid={`checklist-item-row-${i}`}
          >
            <button
              type="button"
              onClick={() => toggleItemFinding(i)}
              className={`flex-shrink-0 inline-flex items-center gap-1 px-2 py-0.5 rounded-md text-[11px] font-semibold transition-colors ${
                it.is_finding
                  ? "bg-amber-500 text-white hover:bg-amber-600"
                  : "bg-emerald-500 text-white hover:bg-emerald-600"
              }`}
              data-testid={`checklist-item-toggle-${i}`}
              title="Vaihda tyyppiä (työvaihe / havainto)"
            >
              {it.is_finding ? <><AlertTriangle className="h-3 w-3" /> Havainto</> : <><CheckCircle2 className="h-3 w-3" /> Toimenpide</>}
            </button>
            <div className="text-sm text-slate-800 flex-1">{it.label}</div>
            <button onClick={() => setItems(items.filter((_, idx) => idx !== i))} className="text-rose-500 hover:text-rose-700" data-testid={`checklist-item-remove-${i}`}>
              <X className="h-4 w-4" />
            </button>
          </div>
        ))}
        <div className="flex items-center gap-2">
          <input
            value={newItem}
            onChange={(e) => setNewItem(e.target.value)}
            onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); addItem(); } }}
            placeholder="Lisää työvaihe tai tarkistuskohta"
            className="input-base flex-1"
            data-testid="checklist-new-item-input"
          />
          <button onClick={addItem} className="btn-ghost text-sm" data-testid="checklist-add-item">
            <Plus className="h-4 w-4" /> Lisää
          </button>
        </div>
      </div>
      <div className="mt-4 flex justify-end gap-2">
        <button onClick={onClose} className="btn-ghost text-sm">Peruuta</button>
        <button onClick={save} disabled={busy} className="btn-primary text-sm disabled:opacity-50" data-testid="checklist-save">
          <Save className="h-4 w-4" /> {busy ? "Tallennetaan…" : "Tallenna"}
        </button>
      </div>
    </div>
  );
}

// ---------------- Materials ----------------
function MaterialsSection() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(null);

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/company/materials");
      setItems(data.materials || []);
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => { load(); }, []);

  const remove = async (id) => {
    if (!confirm("Poistetaanko tämä tarvike?")) return;
    await api.delete(`/company/materials/${id}`);
    toast.success("Poistettu");
    load();
  };

  return (
    <section className="rounded-2xl border border-slate-100 bg-white p-6 shadow-sm" data-testid="company-materials-section">
      <div className="flex items-center gap-3 mb-1">
        <div className="h-9 w-9 rounded-lg bg-blue-50 text-blue-600 flex items-center justify-center">
          <Package className="h-5 w-5" />
        </div>
        <div className="flex-1">
          <div className="font-display font-semibold text-slate-900">Tarvikkeet</div>
          <div className="text-xs text-slate-500">Yrityksen vakiotarvikkeet — valittavissa raportin luonnissa.</div>
        </div>
        {editing === null && (
          <button onClick={() => setEditing({})} className="btn-primary text-sm" data-testid="material-add">
            <Plus className="h-4 w-4" /> Uusi tarvike
          </button>
        )}
      </div>

      {editing !== null && (
        <MaterialEditor
          initial={editing}
          onClose={() => setEditing(null)}
          onSaved={() => { setEditing(null); load(); }}
        />
      )}

      <div className="mt-4 grid sm:grid-cols-2 gap-2">
        {loading && <div className="text-sm text-slate-400 py-3">Ladataan…</div>}
        {!loading && items.length === 0 && editing === null && (
          <div className="text-sm text-slate-500 py-3 italic col-span-2">Ei vielä tarvikkeita. Luo ensimmäinen yläpuolella.</div>
        )}
        {items.map((m) => (
          <div key={m.material_id} className="flex items-center gap-2 rounded-lg border border-slate-100 bg-slate-50/50 px-3 py-2" data-testid={`material-row-${m.material_id}`}>
            <Package className="h-4 w-4 text-blue-600 flex-shrink-0" />
            <div className="flex-1 min-w-0 text-sm font-semibold text-slate-900 truncate">{m.name}</div>
            <span className="text-xs text-slate-500">{m.unit}</span>
            <button onClick={() => setEditing(m)} className="p-1.5 rounded-lg hover:bg-white text-slate-500">
              <Pencil className="h-3.5 w-3.5" />
            </button>
            <button onClick={() => remove(m.material_id)} className="p-1.5 rounded-lg hover:bg-white text-rose-500" data-testid={`material-delete-${m.material_id}`}>
              <Trash2 className="h-3.5 w-3.5" />
            </button>
          </div>
        ))}
      </div>
    </section>
  );
}

function MaterialEditor({ initial, onClose, onSaved }) {
  const [name, setName] = useState(initial.name || "");
  const [unit, setUnit] = useState(initial.unit || "kpl");
  const [busy, setBusy] = useState(false);
  const save = async () => {
    if (!name.trim()) { toast.error("Anna tarvikkeen nimi"); return; }
    setBusy(true);
    try {
      if (initial.material_id) {
        await api.patch(`/company/materials/${initial.material_id}`, { name: name.trim(), unit: unit.trim() || "kpl" });
      } else {
        await api.post("/company/materials", { name: name.trim(), unit: unit.trim() || "kpl" });
      }
      toast.success("Tallennettu");
      onSaved();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Tallennus epäonnistui");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="mt-4 rounded-xl border border-blue-200 bg-blue-50/40 p-4" data-testid="material-editor">
      <div className="grid grid-cols-[1fr_120px] gap-2">
        <input
          autoFocus
          value={name}
          onChange={(e) => setName(e.target.value)}
          placeholder="Tarvikkeen nimi (esim. Suodatin F7)"
          className="input-base"
          data-testid="material-name-input"
        />
        <input
          value={unit}
          onChange={(e) => setUnit(e.target.value)}
          placeholder="yksikkö"
          className="input-base"
          data-testid="material-unit-input"
        />
      </div>
      <div className="mt-3 flex justify-end gap-2">
        <button onClick={onClose} className="btn-ghost text-sm">Peruuta</button>
        <button onClick={save} disabled={busy} className="btn-primary text-sm disabled:opacity-50" data-testid="material-save">
          <Save className="h-4 w-4" /> {busy ? "Tallennetaan…" : "Tallenna"}
        </button>
      </div>
    </div>
  );
}
